// Playtest stats (2026-10-31, Devin: which army and hero get picked, match length, win or loss, where a player quits,
// and frame rate). One event shape for both ends: the game builds events through clean() (src/client/stats.ts) and the
// receiver (functions/api/stats.ts) keeps only what clean() passes, so the two can never disagree about what is sent.
// Nothing about the player: no cookie, no stored id of any kind, no address, no user agent, no screen size, no
// fingerprint. An event is a name and a few fields from fixed lists or bounded numbers; a batch has no id either (its
// rows do land side by side in the table, but nothing names the visit or the device). Sheep Tag's {name, props} shape
// (sheep-tag src/client/analytics.ts). site/privacy.html says all of this in plain words (tests/legal.test.ts).
import { FACTIONS, HEROES } from '../sim/defs';
import { ORIGIN } from '../site/brand';

/** The hidden playtest's host. */
export const PLAYTEST_HOST = 'footmen-wars-playtest.pages.dev';
/** The production host, footmenwars.com (2026-11-01, launch prep: the game ships with stats on). Its www and the alt
 *  domains never serve the game (functions/_middleware.ts 301s them here), and its pages.dev preview stores nothing. */
export const LIVE_HOST = new URL(ORIGIN).hostname;
/** The preview Worker footmen-wars-preview (scripts/deploy.sh --preview, 2026-11-02): its own Worker, rooms and
 *  workers.dev host, so a preview never touches production; its rows are kept apart from live and playtest. */
export const PREVIEW_HOST = 'footmen-wars-preview.tinwhistlegames.workers.dev';
/** The only hosts that send or store events. Their env keeps playtest, preview and production numbers apart
 *  (column env, migrations/0001_footmen_events_env.sql; scripts/stats.ts reads one at a time). */
export const STATS_ENV = { [PLAYTEST_HOST]: 'playtest', [LIVE_HOST]: 'live', [PREVIEW_HOST]: 'preview' } as const;
export type StatsEnv = (typeof STATS_ENV)[keyof typeof STATS_ENV];
export const envOf = (host: string): StatsEnv | null => (Object.hasOwn(STATS_ENV, host) ? STATS_ENV[host as keyof typeof STATS_ENV] : null);
export const STATS_PATH = '/api/stats';
/** A batch's limits: the receiver drops anything bigger. */
export const MAX_BODY = 4000, MAX_EVENTS = 20;

/** Frames a second in five buckets: a number this coarse says how it plays without describing the machine. */
export const FPS_BUCKETS = ['under20', '20to30', '30to45', '45to55', '55up'] as const;
export const fpsBucket = (fps: number) => FPS_BUCKETS[fps < 20 ? 0 : fps < 30 ? 1 : fps < 45 ? 2 : fps < 55 ? 3 : 4];
/** Where a player was when they left: the title, a pick, a match (with its minute), watching bots, the end card. */
export const QUIT_AT = ['title', 'hero_pick', 'army_pick', 'match', 'watch', 'end'] as const;
export type QuitAt = (typeof QUIT_AT)[number];

type Field = readonly string[] | { min: number; max: number } | { text: number };
const INPUT = ['touch', 'mouse'] as const;
/** Which button brought the visit in (2026-11-02, launch week): jump for a Jump in link the game could read (whether
 *  its seat take then worked is html[data-jump], not this), hero for one it could not, the site's Play links by where
 *  they sit (close, nav), the installed app (the manifest's ?src=app), else direct. Only src says which button, and
 *  the game counts it once: main.ts takes src off the address after the visit, so a reload is direct, even one during
 *  a jump's catch-up while its jump keys are still there (Astra re-check, 2026-11-02).
 *  src is a traffic key (TRAFFIC_KEY), so these stay real visits. Discord joins need nothing: the invite counts its uses. */
export const VIA = ['jump', 'hero', 'close', 'nav', 'app', 'direct'] as const;
export type Via = (typeof VIA)[number];
const SRC_VIA: readonly string[] = ['hero', 'close', 'nav', 'app'];
export const viaOf = (q: URLSearchParams, jumped: boolean): Via => {
  const s = q.get('src') ?? '';
  return !SRC_VIA.includes(s) ? 'direct' : s === 'hero' && jumped ? 'jump' : (s as Via);
};
/** Each event's fields and what each may hold. Any field not listed is dropped; any listed field out of its range drops
 *  the whole event. `test` marks a scripted or QA visit (?qa=1 and friends), which scripts/stats.ts leaves out. */
export const EVENTS = {
  visit: { input: INPUT, via: VIA },
  hero: { hero: HEROES },
  army: { army: FACTIONS },
  end: { result: ['win', 'loss'], secs: { min: 0, max: 7200 }, army: [...FACTIONS, 'none'], hero: [...HEROES, 'none'], tier: { min: 0, max: 4 } },
  quit: { at: QUIT_AT, min: { min: 0, max: 120 }, to: ['closed', 'hidden', 'title'] },
  back: { at: QUIT_AT },
  fps: { mid: FPS_BUCKETS, low: FPS_BUCKETS, input: INPUT },
  error: { msg: { text: 160 }, src: { text: 60 }, line: { min: 0, max: 1e7 } },
} satisfies Record<string, Record<string, Field>>;
export type EventName = keyof typeof EVENTS;
/** Fields an event must carry (the rest may be missing). */
const NEEDS: Record<EventName, string[]> = { visit: ['input'], hero: ['hero'], army: ['army'], end: ['result', 'secs'], quit: ['at', 'to'], back: ['at'], fps: ['mid', 'low'], error: ['msg'] };

export type StatEvent = { name: EventName; props: Record<string, string | number> };

/** An event as both ends accept it, or null: a known name, only its own fields, each in range. */
export function clean(e: unknown): StatEvent | null {
  if (!e || typeof e !== 'object') return null;
  const { name, props } = e as { name?: unknown; props?: unknown };
  if (typeof name !== 'string' || !Object.hasOwn(EVENTS, name)) return null;
  const spec = EVENTS[name as EventName] as Record<string, Field>, out: Record<string, string | number> = {};
  const p = props && typeof props === 'object' ? props as Record<string, unknown> : {};
  for (const [k, f] of Object.entries(spec)) {
    const v = p[k];
    if (v === undefined) continue;
    if ('text' in f) { if (typeof v !== 'string') return null; out[k] = v.replace(/https?:\/\/\S+/g, '<url>').slice(0, f.text); }
    else if ('min' in f) { if (typeof v !== 'number' || !Number.isInteger(v) || v < f.min || v > f.max) return null; out[k] = v; }
    else { if (typeof v !== 'string' || !f.includes(v)) return null; out[k] = v; }
  }
  if (p.test === 1) out.test = 1;
  return NEEDS[name as EventName].every((k) => k in out) ? { name: name as EventName, props: out } : null;
}

/** A batch's body as the receiver reads it: at most MAX_EVENTS clean events, or none for anything malformed. */
export function readBatch(body: string): StatEvent[] {
  if (body.length > MAX_BODY) return [];
  let v: unknown;
  try { v = JSON.parse(body); } catch { return []; }
  const list = (v as { events?: unknown })?.events;
  if (!Array.isArray(list) || list.length > MAX_EVENTS) return [];
  return list.map(clean).filter((x): x is StatEvent => x !== null);
}

/** Whether this page may send anything: never with Do Not Track or Global Privacy Control on, and only from a STATS_ENV
 *  host (a local build only with ?stats=1, for scripts/stats-check.ts's stub receiver). */
export function statsAllowed(loc: { hostname: string; search: string }, nav: { doNotTrack?: string | null; globalPrivacyControl?: boolean }, win: { doNotTrack?: string | null } = {}) {
  const dnt = [nav.doNotTrack, win.doNotTrack].some((v) => v === '1' || v === 'yes');
  if (dnt || nav.globalPrivacyControl === true) return false;
  if (envOf(loc.hostname)) return true;
  return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(loc.hostname) && new URLSearchParams(loc.search).get('stats') === '1';
}

/** The address keys a real visitor brings with them: campaign tags, the click ids search engines, ad networks and social
 *  apps add, Google's srsltid on organic results, and src (the installed app opens /play/?src=app, the manifest's
 *  start_url, 2026-11-01). Any other key is one of our own settings (?qa=1, ?seed=, ...). */
const TRAFFIC_KEY = /^(utm_[a-z_]+|fbclid|gclid|gbraid|wbraid|dclid|gad_source|msclkid|ttclid|twclid|rdt_cid|li_fat_id|igshid|mc_cid|mc_eid|srsltid|_gl|ref|src)$/;
/** Whether a visit is our own check, marked test and left out of scripts/stats.ts: its address carries a key no real
 *  visitor brings. Unknown keys count as tests, so a new setting can never leak into the numbers (Flash F3, 2026-11-01:
 *  the old list kept only utm_, fbclid and gclid, so a Bing ad, an iPhone Google ad or a Google result's srsltid
 *  would have dropped a real player). */
export const isTestVisit = (keys: Iterable<string>) => [...keys].some((k) => !TRAFFIC_KEY.test(k));
